import { makeAutoObservable } from "mobx"
import { apiClient } from "api/trpc/trpc-client"
import MarkdownStore from "reusable/markdown/MarkdownStore"
import React, { createRef } from "react"

interface GapImageComponentStoreProps {
   attributes: Record<string, string> | undefined
   markdownStore: MarkdownStore
   updateMdastNode: (node: any) => void
}

export default class GapImageComponentStore {
   sp
   fileName = ''
   rawFileName = ''
   downloadUrl: string | null = null
   loading = true
   error = false
   width: number | null = null
   height: number | null = null
   hovered = false
   imgRef = createRef<HTMLImageElement>()
   containerRef = createRef<HTMLDivElement>()

   sourceType: string | undefined
   sourceEntityId: string | undefined

   constructor(sp: GapImageComponentStoreProps) {
      makeAutoObservable(this, {
         // React refs should not be observed
         imgRef: false,
         containerRef: false,
      })
      this.sp = sp
      this.fileName = sp.attributes?.name || ''
      this.rawFileName = sp.attributes?.rawFileName || ''
      this.sourceType = sp.attributes?.sourceType
      this.sourceEntityId = sp.attributes?.sourceEntityId
      const parsedWidth = Number(sp.attributes?.width)
      this.width = parsedWidth > 0 ? parsedWidth : null
      const parsedHeight = Number(sp.attributes?.height)
      this.height = parsedHeight > 0 ? parsedHeight : null
   }

   getDownloadUrl = async () => {
      try {
         const url = await apiClient.markdown.getFileDownloadUrl.query({
            fileName: this.rawFileName,
            entityId: this.sp.markdownStore.sp.id,
            ...(this.sourceType === 'blueprintBlockContent' && this.sourceEntityId
               ? { sourceType: this.sourceType, sourceEntityId: this.sourceEntityId }
               : {}),
         })
         this.downloadUrl = url
      } catch {
         this.error = true
      } finally {
         this.loading = false
      }
   }

   handleImageError = () => {
      this.error = true
   }

   handleMouseEnter = () => {
      this.hovered = true
   }

   handleMouseLeave = () => {
      this.hovered = false
   }

   handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const img = this.imgRef.current
      const container = this.containerRef.current
      if (!img || !container) return

      const handleIndex = Number((e.currentTarget as HTMLElement).dataset.handleIndex)
      const startX = e.clientX
      const startWidth = img.getBoundingClientRect().width
      const aspectRatio = img.naturalWidth / img.naturalHeight
      const maxWidth = container.getBoundingClientRect().width
      const isLeft = handleIndex === 0 || handleIndex === 2

      document.body.style.userSelect = 'none'

      const onPointerMove = (moveEvent: PointerEvent) => {
         const dx = moveEvent.clientX - startX
         const widthDelta = isLeft ? -dx : dx
         const newWidth = Math.max(100, Math.min(maxWidth, startWidth + widthDelta))
         img.style.width = `${newWidth}px`
         img.style.height = `${newWidth / aspectRatio}px`
      }

      const onPointerUp = () => {
         document.removeEventListener('pointermove', onPointerMove)
         document.removeEventListener('pointerup', onPointerUp)
         document.body.style.userSelect = ''

         const rect = img.getBoundingClientRect()
         this.handleResizeEnd(rect.width, rect.height)
      }

      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', onPointerUp)
   }

   handleResizeEnd = (newWidth: number, newHeight: number) => {
      this.width = newWidth
      this.height = newHeight
      this.sp.updateMdastNode({
         attributes: {
            ...this.sp.attributes,
            width: String(Math.round(newWidth)),
            height: String(Math.round(newHeight)),
         },
      })
   }
}
